import { PAGES } from '$lib/utils/constants';

export default class APIError extends Error {
	/**
	 * API Custom Error class for handling specific error cases.
	 * @param {string|Error} inputError - The error message string or original Error object.
	 * @param {number} [errorCode] - A custom error code.
	 * @param {object} [details] - Optional additional details about the error.
	 */
	constructor(inputError, errorCode, details) {
		const processedError = APIError._processError(inputError, errorCode);
		const enhancedErrorMessage = APIError.getEnhancedErrorMessage(processedError.errorCode);

		super(enhancedErrorMessage);

		this.name = this.constructor.name;
		this.errorCode = processedError.errorCode;
		this.details = details;
		this.orignalMessage = processedError.originalMessage;
		this.enhancedErrorMessage = enhancedErrorMessage;
		this.cause = {
			message: processedError.message,
			errorCode: processedError.errorCode,
			details
		};

		APIError._addNetworkDiagnostics(this, inputError);

		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, this.constructor);
		}
	}

	/**
	 * Custom toString method to ensure proper serialization for logging and Sentry
	 * @returns {string} The error message
	 */
	toString() {
		return this.message || this.enhancedErrorMessage || 'APIError';
	}

	/**
	 * Custom toJSON method to ensure proper serialization
	 * @returns {object} Serializable representation of the error
	 */
	toJSON() {
		return {
			name: this.name,
			message: this.message,
			errorCode: this.errorCode,
			enhancedErrorMessage: this.enhancedErrorMessage,
			originalMessage: this.orignalMessage,
			details: this.details,
			stack: this.stack,
			cause: this.cause
		};
	}

	/**
	 * Processes the input and determines the appropriate message and error code.
	 * @param {string|Error} inputError - The input message string or error object.
	 * @param {number} [errorCode] - Optional error code.
	 * @returns {{message: string, errorCode: number, originalMessage: string}} Processed error information.
	 * @private
	 */
	static _processError(inputError, errorCode) {
		const isErrorObject = APIError._isErrorObject(inputError);

		if (isErrorObject && APIError._isFetchError(/** @type {Error} */ (inputError))) {
			return APIError._processFetchError(/** @type {Error} */ (inputError), errorCode);
		}

		return {
			message: typeof inputError === 'string' ? inputError : inputError?.message || 'unknown',
			errorCode: errorCode || APIError.UNKNOWN_API_ERROR.code,
			originalMessage:
				typeof inputError === 'string' ? inputError : inputError?.message || 'unknown'
		};
	}

	/**
	 * Checks if the input is an error object.
	 * @param {any} input - The input to check.
	 * @returns {boolean} True if it's an error object.
	 * @private
	 */
	static _isErrorObject(input) {
		return input && typeof input === 'object' && (input.name || input.message);
	}

	/**
	 * Checks if an error is a fetch-related error.
	 * @param {Error} error - The error to check.
	 * @returns {boolean} True if it's a fetch error.
	 * @private
	 */
	static _isFetchError(error) {
		return error.name === 'TypeError' || /Failed to fetch/i.test(String(error));
	}

	/**
	 * Processes fetch errors and returns appropriate error information.
	 * @param {Error} error - The fetch error.
	 * @param {number} [errorCode] - Optional error code.
	 * @returns {{message: string, errorCode: number, originalMessage: string}} Processed fetch error information.
	 * @private
	 */
	static _processFetchError(error, errorCode) {
		const reason = /Failed to fetch/i.test(String(error)) ? 'network-failed-to-fetch' : 'unknown';

		return {
			message: `fetch-failed: ${reason}`,
			errorCode: errorCode || APIError.FETCH_ERROR_CODE,
			originalMessage: error.message || 'unknown'
		};
	}

	/**
	 * Adds network diagnostics to the error instance.
	 * @param {APIError} errorInstance - The error instance to modify.
	 * @param {string|Error} inputError - The original input error.
	 * @private
	 */
	static _addNetworkDiagnostics(errorInstance, inputError) {
		if (
			APIError._isErrorObject(inputError) &&
			APIError._isFetchError(/** @type {Error} */ (inputError))
		) {
			const isOffline = typeof navigator !== 'undefined' ? !navigator.onLine : undefined;

			// @ts-ignore
			errorInstance.offline = isOffline;
			// @ts-ignore
			errorInstance.cause.originalError = inputError;

			// Mark offline network errors as non-fatal to prevent Sentry spam
			if (isOffline) {
				// @ts-ignore
				errorInstance.metadata = { fatal: false };
			}
		}
	}

	/**
	 * @param {number|string} errorCode - The error code to get the enhanced error message for.
	 * @returns {string} The enhanced error message.
	 */
	static getEnhancedErrorMessage(errorCode) {
		if (!errorCode) return APIError.UNKNOWN_API_ERROR.message;

		const numericErrorCode = typeof errorCode === 'string' ? parseInt(errorCode, 10) : errorCode;
		const foundError = APIError.API_ERROR_CODE_MAPPING.find(
			(error) => error.code === numericErrorCode
		);

		if (foundError) {
			return foundError.message;
		} else {
			return APIError.UNKNOWN_API_ERROR.message;
		}
	}

	static API_ERROR_CODE_MAPPING = [
		{
			code: 992302,
			message: 'Your payment was declined. Please try again or use a different payment method.'
		},
		{
			code: 992100,
			message: 'Your payment was declined. Please try again or use a different payment method.'
		},
		{
			code: 992000,
			message: 'The email or password you have entered is incorrect.'
		},
		{
			code: 992316,
			message: `The email you entered is already associated with an account. Please log in <a id="error-existingLoginLink" class="text-electricBlue underline" href=${PAGES.INTERNAL.LOGIN}>here</a> or use a different email.`
		},
		{
			code: 992315,
			message: `The email you entered is already associated with an account. Please log in <a id="error-existingLoginLink" class="text-electricBlue underline" href=${PAGES.INTERNAL.LOGIN}>here</a> or use a different email.`
		},
		{
			code: 992601,
			message: `The email you entered is already associated with an account. Please log in <a id="error-existingLoginLink" class="text-electricBlue underline" href=${PAGES.INTERNAL.LOGIN}>here</a> or use a different email.`
		},
		{
			code: 992602,
			message: `The email you entered is already associated with an account. Please log in <a id="error-existingLoginLink" class="text-electricBlue underline" href=${PAGES.INTERNAL.LOGIN}>here</a> or use a different email.`
		},
		{
			code: 992603,
			message: `The email you entered is already associated with an account. Please log in <a id="error-existingLoginLink" class="text-electricBlue underline" href=${PAGES.INTERNAL.LOGIN}>here</a> or use a different email.`
		},
		{
			code: 992501,
			message: `The account you are trying to access is not a Legacy account. Please log in <a id="error-existingLoginLink" class="text-electricBlue underline" href=${PAGES.INTERNAL.LOGIN}>here</a> or use a different email.`
		},
		{
			code: 992323,
			message:
				'We currently cannot accept prepaid cards. Please try another card or payment method.'
		}
	];

	static UNKNOWN_API_ERROR = {
		code: 100000,
		message: 'Sorry, an unknown error occurred. Please try again later.'
	};

	static FETCH_ERROR_CODE = 100001;
}
